import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { HelpCircleIcon, PlusIcon, PencilIcon, XIcon } from 'lucide-react';

import { queryClient } from 'api';
import { useToaster, ToasterVariant } from 'hooks/useToaster';
import { cn } from '@epoch-ui/lib/utils';
import { Button } from '@epoch-ui/components/Button';
import { Badge } from '@epoch-ui/components/Badge/badge';
import { Tooltip, TooltipTrigger, TooltipContent } from '@epoch-ui/components/Tooltip';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';
import { eventCategoriesApi } from '@event-editor/api/eventCategories';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';

import { ManageEventCategoriesModal } from './ManageEventCategoriesModal';

const EditEventCategories: React.FC = () => {
  const event = useEditableEvent();
  const { showToast } = useToaster();

  const [isManageCategoriesModalOpen, setIsManageCategoriesModalOpen] = useState(false);

  const updateEventCategoriesMutation = useMutation({
    mutationFn: (categoryIds: number[]) =>
      eventCategoriesApi.updateEventCategories({ eventId: event.id!, categoryIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: eventEditorQueryKeys.event(event.id!) });
      setIsManageCategoriesModalOpen(false);
    },
    onError: (error) => {
      console.error('Failed to update event categories:', error);
      showToast('Failed to update event categories.', { variant: ToasterVariant.ERROR });
    }
  });

  const isCancelled = !!event.scheduledDeletionAt;

  return (
    <div className={cn(isCancelled && 'tw-opacity-40')}>
      <div className="tw-mb-4 tw-flex tw-flex-col tw-items-start">
        {/* Heading and description */}
        <div className="tw-mb-5 tw-space-y-1">
          <h2 className="tw-m-0 tw-text-xl tw-font-medium tw-leading-7">Categories</h2>
          <div className="tw-text-sm tw-leading-5 tw-text-muted-foreground">
            Make it easy for employees to find your event{' '}
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircleIcon
                  tabIndex={0}
                  role="button"
                  type="button"
                  aria-label="Learn more about categories"
                  size={16}
                  className="tw-ml-1"
                />
              </TooltipTrigger>
              <TooltipContent className="tw-w-80 tw-gap-2">
                <p className="tw-text-xs tw-font-normal tw-leading-4">
                  Categories are tags for grouping similar events together. Selecting categories
                  helps employees search for events and groups data for reporting.
                </p>
                <a
                  href="https://help.epochapp.com/articles/what-are-categories"
                  target="_blank"
                  rel="noreferrer"
                  className="tw-text-primary-foreground tw-underline hover:tw-text-primary-foreground"
                >
                  Learn more
                </a>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>

        {/* Categories */}
        <div className="tw-mb-4 tw-flex tw-flex-wrap tw-gap-2">
          {(event.categories?.length || 0) > 0
            ? // Event categories
              event.categories?.map((category) => (
                <Badge key={category.id} className="tw-font-normal">
                  {category.name}
                  <button
                    type="button"
                    className="tw-m-0 tw-ml-1 tw-p-0 tw-text-inherit"
                    aria-label={`Remove ${category.name} category`}
                    disabled={isCancelled}
                    onClick={() => {
                      updateEventCategoriesMutation.mutateAsync([
                        ...(event.categories || [])
                          .filter((c) => c.id !== category.id)
                          .map((c) => Number(c.id))
                      ]);
                    }}
                  >
                    <XIcon size={16} />
                  </button>
                </Badge>
              ))
            : // Placeholder badges
              Array.from({ length: 3 }).map((_, index) => (
                <Badge
                  variant="outline"
                  key={index}
                  className="tw-w-20 tw-items-center tw-rounded-xl tw-border-dashed"
                >
                  <PlusIcon size={16} className="tw-mx-auto" />
                </Badge>
              ))}
        </div>

        {/* Add/Edit button */}
        <Button
          variant="secondary"
          size="xxs"
          className="tw-rounded-xl disabled:tw-opacity-100"
          disabled={isCancelled}
          onClick={(): void => setIsManageCategoriesModalOpen(true)}
        >
          {event.categories?.length ? (
            <>
              <PencilIcon size={16} className="tw-mr-1" />
              Edit categories
            </>
          ) : (
            <>
              <PlusIcon size={16} className="tw-mr-1" />
              Add category
            </>
          )}
        </Button>
      </div>

      <ManageEventCategoriesModal
        open={isManageCategoriesModalOpen}
        onOpenChange={setIsManageCategoriesModalOpen}
        onSubmit={(categories): Promise<unknown> => {
          return updateEventCategoriesMutation.mutateAsync(
            categories.map((category) => Number(category.id))
          );
        }}
      />
    </div>
  );
};

export { EditEventCategories };
